import React, { useEffect, useState } from "react";
import { Button, Col, Container, Row, Spinner, Stack } from "react-bootstrap";
import {
  deleteReportDashboardCharts,
  fetchCaseRange,
  fetchJobs,
  fetchPopulation,
  fetchPrograms,
  fetchReportGoals,
  fetchReportInteractions,
  fetchReportJobs,
  fetchReportPrograms,
  fetchReportResources,
  fetchReportShelterIntakesExits,
  fetchReportShelterOccupancy,
  fetchReportTasks,
  patchReportDashboardCharts,
  postReportDashboardCharts,
  reorderReportDashboardCharts,
} from "../../services/httpClient";
import { faClose, faEdit, faExpand } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { dashboardMap } from "./ui/DashboardMap";
import { Tooltip } from "react-tooltip";
import SelectObjectTypeahead from "../ui/SelectObjectTypeahead";
import ReportTimeInterval from "./ui/ReportTimeInterval";
import { activeKey } from "./Reports";
import { formatDateTimeToLocal } from "../../helpers/date";

const Dashboard = ({ setShowDashboard, myDashboardCharts, getMyDashboardCharts, reportCharts, iPad, tabOrder }: any) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [availableCharts, setAvailableCharts] = useState<any>();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [selectedChart, setSelectedChart] = useState<any>();
  const [mappedChart, setMappedChart] = useState<any>({});
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const [draggedProgramId, setDraggedProgramId] = useState<number | null>(null);
  const [errMsg, setErrMsg] = useState("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [dateInterval, setDateInterval] = useState<number>(365);
  const [showDateTimePicker, setShowDateTimePicker] = useState<boolean>(false);
  const [dataMap, setDataMap] = useState<any>(undefined);

  useEffect(() => {
    if (activeKey.value === "dashboard") {
      setIsLoading(true);
      let startDate = new Date();
      startDate.setDate(startDate.getDate() - dateInterval);
      const start = startDate.toISOString().slice(0, 10);
      setStartDate(start);
      const end = new Date().toISOString().slice(0, 10);
      setEndDate(end);

      if (myDashboardCharts?.length === 0) {
        setEditMode(true);
      } else {
        //get data for each category of metric
        getCategoryData(start, end, myDashboardCharts);
        setShowDashboard(false);
        myDashboardCharts?.map((chart: any) => {
          const usePicker = dashboardMap[chart.chart_key]?.useDateTimePicker;
          if (usePicker) {
            setShowDateTimePicker(true);
            return true;
          }
          return false;
        });
      }

      setErrMsg("");
      setAvailableCharts(reportCharts);
      setIsLoading(false);
    }
  }, [activeKey.value]);

  const getCategoryData = async (start: string, end: string, myCharts: any = myDashboardCharts) => {
    //categories we need to get data to display
    const categories: string[] = Array.from(new Set(myCharts?.map((item: any) => item.category)));
    const promises: { key: string; promise: Promise<any> }[] = [];

    categories.forEach(async (cat: string) => {
      switch (cat) {
        case "interactions":
          promises.push({ key: "interactions", promise: getInteractions(start, end) });
          break;
        case "goals and tasks":
          promises.push({ key: "goals and tasks", promise: getGoalsAndTasksData(start, end) });
          break;
        case "assessments":
          break;
        case "shelter":
          promises.push({ key: "shelter", promise: getShelterData() });
          break;
        case "programming":
          promises.push({ key: "programming", promise: getProgramData() });
          break;
        case "job assignments":
          promises.push({ key: "job assignments", promise: getJobData() });
          break;
        case "resources":
          promises.push({ key: "resources", promise: getResourceData(start, end) });
          break;
      }
    });

    const results = await Promise.all(promises.map((p) => p.promise));
    const resolved: Record<string, any> = {};
    promises.forEach((p, i) => {
      resolved[p.key] = results[i];
    });
    const notes = resolved["interactions"] ?? {};
    const { caseRange, goals, tasks } = resolved["goals and tasks"] ?? {};
    const { occupancy, intakes, exits } = resolved["shelter"] ?? {};
    const { programs, programData } = resolved["programming"] ?? {};
    const { departments, jobs, assignments } = resolved["job assignments"] ?? {};
    const { resources, population } = resolved["resources"] ?? {};

    const map = {
      interactions: {
        notes,
      },
      "goals and tasks": {
        caseRange,
        goals,
        tasks,
      },
      shelter: {
        occupancy,
        intakes,
        exits,
        start,
        end,
      },
      programming: {
        programs,
        programData,
      },
      "job assignments": {
        departments,
        jobs,
        assignments,
      },
      resources: {
        resources,
        population,
      },
    };
    setDataMap(map);
  };

  //interactions
  const getInteractions = async (start: string, end: string) => {
    // Request the aggregated summary for fast chart rendering
    return fetchReportInteractions(start, end, true)
      .then(async (res: any) => {
        const notes = res[0].data || {};
        return notes;
      })
      .catch((err: any) => {
        console.log(err);
        return null;
      });
  };

  //goals and tasks
  const getGoalsAndTasksData = async (start: string, end: string) => {
    const [caseRange, goals, tasks] = await Promise.all([getCaseRange(start, end), getGoals(start, end), getTasks(start, end)]);
    return { caseRange, goals, tasks };
  };

  const getCaseRange = (start: string, end: string) => {
    return fetchCaseRange(start, end)
      .then((res: any) => {
        const cases = res[0].data;
        return cases;
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const getGoals = (start: string, end: string) => {
    return fetchReportGoals(start, end)
      .then((res: any) => {
        const goalData = res[0].data;
        const goals = goalData.sort((a: any, b: any) => (a.completed_at < b.completed_at ? 1 : -1));
        goals.forEach((goal: any) => {
          if (goal?.completed_at) {
            const localDateTime = formatDateTimeToLocal(goal.completed_at);
            goal.completed_at_formatted = localDateTime.date + " " + localDateTime.time;
          }
          if (goal?.created_at) {
            const localDateTime = formatDateTimeToLocal(goal.created_at);
            goal.created_at_formatted = localDateTime.date + " " + localDateTime.time;
          }
        });
        return goals;
      })
      .catch((err: any) => {
        console.log(err);
        return null;
      });
  };

  const getTasks = (start: string, end: string) => {
    return fetchReportTasks(start, end)
      .then((res: any) => {
        const taskData = res[0].data;
        const tasks = taskData.sort((a: any, b: any) => (a.cm_reviewed_at < b.cm_reviewed_at ? 1 : -1));
        tasks.forEach((task: any) => {
          const localDateTime = formatDateTimeToLocal(task.cm_reviewed_at);
          task.cm_reviewed_at_formatted = localDateTime.date + " " + localDateTime.time;
        });
        return tasks;
      })
      .catch((err: any) => {
        console.log(err);
        return null;
      });
  };

  //shelter
  const getShelterData = async () => {
    const [occupancy, intakeExit] = await Promise.all([getOccupancyData(), getIntakeExitData()]);
    return { occupancy, intakes: intakeExit?.intakes, exits: intakeExit?.exits };
  };

  const getOccupancyData = () => {
    return fetchReportShelterOccupancy()
      .then((res: any) => {
        const occupancy = res[0].data;
        return occupancy;
      })
      .catch((err: any) => {
        console.log(err);
        return null;
      });
  };

  const getIntakeExitData = () => {
    return fetchReportShelterIntakesExits()
      .then((res: any) => {
        const intakes = res[0].data;
        const exitFiltered = intakes.filter((item: any) => item.exit_at);
        let exits = exitFiltered.map((item: any) => ({
          exit_at: item.exit_at,
          exit_note_id: item.exit_note_id,
          exit_type: item.exit_type,
        }));
        return { intakes, exits };
      })
      .catch((err: any) => {
        console.log(err);
        return null;
      });
  };

  //programming
  const getProgramData = async () => {
    const [programming] = await Promise.all([getPeportPrograms()]);
    return { programs: programming?.programs, programData: programming?.programData };
  };

  const getPrograms = async (programData: any) => {
    return fetchPrograms()
      .then((res: any) => {
        const programs = res[0]?.data ?? [];

        const flattenedProgs = programs.flatMap((prog: any) => {
          const levels = prog.programLevels ?? [];

          // Add level name to each programData entry
          programData?.forEach((d: any) => {
            if (d.program_id === prog.id) {
              const level = levels.find((l: any) => l.id === d.level_id);
              d.level = level?.name;
            }
          });

          const levelEntries = levels.map((level: any) => {
            const levelCount = programData?.filter((d: any) => d.level_id === level.id).length;

            return {
              ...prog,
              id: `${prog.id}-${level.id}`,
              level_id: level.id,
              program_id: prog.id,
              name: `${prog.name} - ${level.name} (${levelCount})`,
              key: `${prog.id}-${level.id}`,
            };
          });

          // Always include the base program entry
          const baseCount = programData?.filter((d: any) => d.program === prog.name).length;
          const baseProgramEntry = {
            ...prog,
            id: prog.id,
            program_id: prog.id,
            name: `${prog.name} (${baseCount})`,
            key: prog.id,
          };
          return [baseProgramEntry, ...levelEntries];
        });

        return flattenedProgs;
      })
      .catch((err: any) => {
        console.log(err);
        return null;
      });
  };

  const getPeportPrograms = async () => {
    return fetchReportPrograms()
      .then(async (res: any) => {
        const programData = res[0]?.data ?? [];
        const programs = await getPrograms(programData);
        return { programs, programData };
      })
      .catch((err: any) => {
        console.log(err);
        return null;
      });
  };

  //jobs
  const getJobData = async () => {
    const [jobData] = await Promise.all([getReportJobs()]);
    return { departments: jobData?.jobDepartments?.departments, jobs: jobData?.jobDepartments?.jobs, assignments: jobData?.assignments };
  };

  const getJobs = async (deptCounts: any) => {
    return fetchJobs()
      .then((res: any) => {
        const jobs = res[0].data;

        const deptMap = new Map<string, any>();
        jobs.map((j: any) => {
          const deptID = j.department_id;
          const deptName = j.department;

          const count = deptCounts[deptName] ?? 0;
          if (!deptMap.has(deptName)) {
            deptMap.set(deptName, { id: deptID, name: `${deptName} (${count})`, key: deptName, count });
          }

          const open = j.positions_total - j.positions_filled;
          return {
            ...j,
            positions_open: open,
          };
        });

        Object.entries(deptCounts).forEach(([dept, count]) => {
          const key = dept;
          if (!deptMap.has(key)) {
            deptMap.set(key, { name: `${key} (${count})`, key, count });
          } else {
            const existing = deptMap.get(key);
            existing.count = count;
            existing.name = `${key} (${count})`;
          }
        });

        const departments = Array.from(deptMap.values()).sort((a: any, b: any) => a.key.localeCompare(b.key));

        const jobDepartments = { departments, jobs };
        return jobDepartments;
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const getReportJobs = async () => {
    return fetchReportJobs()
      .then(async (res: any) => {
        const assignments = res[0].data;
        const deptCounts = assignments.reduce((acc: Record<string, number>, job: any) => {
          const dept = job.department;
          acc[dept] = (acc[dept] || 0) + 1;
          return acc;
        }, {});

        const jobDepartments = await getJobs(deptCounts);
        return { jobDepartments, assignments };
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  //resources
  const getResourceData = async (start: string, end: string) => {
    const [resources, population] = await Promise.all([getResourceAssignments(start, end), getPopulation(start, end)]);
    return { resources, population };
  };

  const getResourceAssignments = async (start: string, end: string) => {
    return fetchReportResources(start, end)
      .then((res: any) => {
        const resources = res[0].data;
        return resources;
      })
      .catch((err: any) => {
        console.log(err);
        return null;
      });
  };

  const getPopulation = async (start: string, end: string) => {
    return fetchPopulation(start, end)
      .then((res: any) => {
        const population = res[0].data;
        return population;
      })
      .catch((err: any) => {
        console.log(err);
        return null;
      });
  };

  const updateSelectedChart = (e: any) => {
    const formatted = {
      ...e,
      label: e.name,
      value: e.key,
    };
    setSelectedChart(formatted as any);
    setMappedChart(dashboardMap[e?.key]);
  };

  const onClickAddChart = () => {
    const newChart = {
      chart_key: selectedChart?.key,
      col_size: mappedChart.defaultCols,
    };

    postReportDashboardCharts(newChart)
      .then((res: any) => {
        setSelectedChart(null);
        setMappedChart({});
        setShowDateTimePicker(false);
        getMyDashboardCharts();
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
      });
  };

  const onClickDoneEdit = () => {
    setSelectedChart(null);
    setMappedChart(null);
    setErrMsg("");

    if (myDashboardCharts.length === 0) {
      activeKey.value = "interactions";
      setShowDashboard(true);
    } else {
      if (editMode) {
        let startDate = new Date();
        startDate.setDate(startDate.getDate() - 365);
        const start = startDate.toISOString().slice(0, 10);
        setStartDate(start);
        const end = new Date().toISOString().slice(0, 10);
        setEndDate(end);
        getCategoryData(start, end);
        myDashboardCharts?.map((chart: any) => {
          const usePicker = dashboardMap[chart.chart_key]?.useDateTimePicker;
          if (usePicker) {
            setShowDateTimePicker(true);
            return true;
          }
          return false;
        });
      }
      setEditMode((prev: any) => !prev);
    }
  };

  const onClickDeleteChart = (id: number) => {
    deleteReportDashboardCharts(id)
      .then((res: any) => {
        setShowDateTimePicker(false);
        getMyDashboardCharts();
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  useEffect(() => {
    setAvailableCharts(reportCharts);
  }, [reportCharts]);

  const onClickExpandChart = (chart: any, size: string) => {
    patchReportDashboardCharts(chart?.id, size)
      .then((res: any) => {
        getMyDashboardCharts();
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const handleDrop = (dropIndex: number) => {
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const reordered = [...myDashboardCharts];
    const [moved] = reordered.splice(draggedIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    updateChartOrder(reordered);
  };

  const updateChartOrder = (newOrder: any) => {
    reorderReportDashboardCharts(newOrder)
      .then(() => {
        setDraggedIndex(null);
        getMyDashboardCharts(startDate, endDate);
      })
      .catch((err) => {
        setErrMsg(err?.response?.data?.msg);
      });
  };

  return (
    <>
      {isLoading ? (
        <Container className="div-main">
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <div id="dashboard" className="mb-5">
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <Row id="dashboard-title" className="mb-3">
            <Col className="col-8">
              <h4 className="hero-heading app-header-text">{editMode ? "Edit Dashboard" : "Dashboard"}</h4>
            </Col>
            <Col className="text-end col-4">
              {!iPad && (
                <Button className="app-primary-color button" onClick={() => onClickDoneEdit()}>
                  {editMode ? "Close" : <FontAwesomeIcon icon={faEdit} />}
                </Button>
              )}
            </Col>
          </Row>
          {editMode && (
            <>
              <Row id="programs-filters" className="mb-3">
                <Col></Col>
                <Col xs={12} lg={7} className="mb-1 d-flex align-items-center justify-content-center">
                  <Stack direction="horizontal" gap={3} className="w-100">
                    <SelectObjectTypeahead
                      label="Available Charts or Metrics"
                      value={selectedChart}
                      options={availableCharts}
                      onUpdate={(e: any) => updateSelectedChart(e)}
                      cssClasses="w-75 z-2"
                      groupHeading
                    />

                    <Button
                      className="app-tertiary-color button w-25"
                      disabled={selectedChart ? false : true}
                      onClick={() => onClickAddChart()}
                    >
                      Add To Dashboard
                    </Button>
                  </Stack>
                </Col>
                <Col></Col>
              </Row>
              {mappedChart && (
                <Row className="mb-3 d-flex align-items-center justify-content-center px-0">
                  <Col key={selectedChart?.key} xs={mappedChart?.defaultCols} className="mx-0">
                    {mappedChart?.component && <mappedChart.component {...mappedChart?.defaultProps} />}
                  </Col>
                </Row>
              )}
              <Row className="mt-3">
                <hr />
              </Row>
            </>
          )}
          {!editMode && showDateTimePicker && (
            <Row>
              <Col>
                <ReportTimeInterval
                  startDate={startDate}
                  setStartDate={setStartDate}
                  endDate={endDate}
                  setEndDate={setEndDate}
                  dateInterval={dateInterval}
                  setDateInterval={setDateInterval}
                  intervalOne={30}
                  intervalOneText={"Last Month"}
                  intervalTwo={90}
                  intervalTwoText={"Last 3 Months"}
                  intervalThree={180}
                  intervalThreeText={"Last 6 Months"}
                  intervalFour={365}
                  intervalFourText={"Last Year"}
                  onClickRefresh={getCategoryData}
                  hideBtnId="dashboard-refresh-button"
                />
              </Col>
            </Row>
          )}
          <div className="dashboard-masonry">
            {myDashboardCharts?.map((chart: any, index: number) => {
              const mappedChart = dashboardMap[chart.chart_key];
              if (!mappedChart) return <div>Unknown Chart</div>;

              const { component: ChartComponent, defaultCols, maxCols, defaultProps } = mappedChart;

              return (
                <div
                  key={chart?.id}
                  style={{
                    gridColumn: `span ${chart?.col_size}`,
                    padding: ".5rem",
                    alignSelf: "start",
                  }}
                >
                  {editMode ? (
                    <div className="position-relative">
                      <Button
                        data-tooltip-id="tooltip-remove"
                        data-tooltip-content="Remove"
                        className="app-tertiary-color-inverse button position-absolute top-0 end-0 me-3 mt-3 z-1"
                        onClick={() => onClickDeleteChart(chart.id)}
                      >
                        <FontAwesomeIcon icon={faClose} />
                      </Button>
                      <Tooltip id="tooltip-remove" place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                      <div
                        data-tooltip-id="tooltip-chart"
                        data-tooltip-content="Drag to Move"
                        className={dropTargetIndex === index ? "drop-target" : "dashboard-edit"}
                        key={chart.id}
                        draggable
                        onDragStart={(e) => {
                          setDraggedIndex(index);
                          setDraggedProgramId(chart.id);
                          e.currentTarget.classList.add("dragging");
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          if (draggedProgramId !== chart.id) {
                            setDropTargetIndex(index);
                          } else {
                            setDropTargetIndex(null);
                          }
                        }}
                        onDragLeave={() => setDropTargetIndex(null)}
                        onDrop={() => {
                          if (draggedProgramId !== chart.id) {
                            handleDrop(index);
                          }
                          setDropTargetIndex(null);
                          setDraggedProgramId(null);
                        }}
                        onDragEnd={() => {
                          document.querySelectorAll(".dragging").forEach((el) => el.classList.remove("dragging"));
                          setDraggedIndex(null);
                          setDraggedProgramId(null);
                          setDropTargetIndex(null);
                        }}
                      >
                        <ChartComponent {...defaultProps} />
                      </div>
                      <Tooltip id="tooltip-chart" place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                      <Button
                        data-tooltip-id="tooltip-expand"
                        data-tooltip-content="Grow/Shrink"
                        className="app-tertiary-color-inverse button position-absolute bottom-0 end-0 me-3 mb-3 z-1"
                        onClick={() => onClickExpandChart(chart, chart.col_size == defaultCols ? maxCols : defaultCols)}
                      >
                        <FontAwesomeIcon icon={faExpand} />
                      </Button>
                      <Tooltip id="tooltip-expand" place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                    </div>
                  ) : (
                    <div className="position-relative">
                      <div key={index}>
                        <ChartComponent {...defaultProps} data={dataMap?.[chart?.category as keyof typeof dataMap]} useDatePicker={true} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default Dashboard;
